import { Check, ExpandMore } from "@mui/icons-material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
	Box,
	CircularProgress,
	Divider,
	FormControl,
	InputBase,
	MenuItem,
	Select,
	SelectChangeEvent,
	SelectProps,
	Theme,
	Typography,
	styled,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaCheckbox from "../BriaCheckbox/BriaCheckbox.tsx";
import BriaTooltip, { BriaTooltipProps } from "../BriaTooltip/BriaTooltip.tsx";
import DebouncedInput from "../DebouncedInput/DebouncedInput.tsx";
import EditableChip from "../EditableChip/EditableChip.tsx";
import styles from "./BriaDropDown.module.scss";

export type DropDownItem<T = any, ExtraData = any> = {
	key: string;
	value: T;
	icon?: any;
	disabled?: boolean;
	badge?: string;
	badgeType?: "primary" | "secondary";
	customItem?: ReactNode;
	customSelectedKey?: ReactNode;
	className?: string;
	subItems?: DropDownItem<T>[];
	extraData?: ExtraData;
	tooltip?: string;
	tooltipProps?: Partial<BriaTooltipProps>;
	defaultSubItemValue?: T;
};

export type BriaDropDownProps<T> = {
	value?: T;
	handleDelete?: (key: any) => void;
	handleEditItem?: (key: any, value: any) => void;
	searchValue?: string;
	onSearchChange?: (event: any) => void;
	onItemsLoad?: () => void;
	loading?: boolean;
	items: DropDownItem<T>[];
	height?: string;
	maxHeight?: string;
	menuMaxHeight?: string;
	menuMaxWidth?: string;
	menuWidth?: string;
	width?: string;
	className?: string;
	disabled?: boolean;
	hideArrowIcon?: boolean;
	border?: string;
	icon?: any;
	selectedIcon?: any;
	searchable?: boolean;
	debounceSearch?: boolean;
	labelFontWeight?: number;
	labelFontSize?: string;
	placeholderBold?: boolean;
	SearchPlaceholder?: string;
	searchIcon?: boolean;
	minHeight?: string;
	selectMultipleCheckbox?: boolean;
	activeItems?: string[];
	addNoneOption?: boolean;
	endStickyItem?: ReactNode;
} & SelectProps<T>;

const BriaDropdown = <T,>({
	value,
	handleDelete,
	handleEditItem,
	searchValue,
	onSearchChange,
	onItemsLoad,
	loading,
	items,
	width,
	height,
	maxHeight,
	menuMaxHeight,
	menuMaxWidth,
	menuWidth,
	className,
	disabled,
	hideArrowIcon,
	border,
	icon,
	selectedIcon,
	searchable = false,
	debounceSearch = false,
	labelFontWeight,
	labelFontSize,
	SearchPlaceholder = "",
	searchIcon = false,
	minHeight = "",
	selectMultipleCheckbox = false,
	activeItems = [],
	addNoneOption = false,
	endStickyItem,
	...rest
}: BriaDropDownProps<T>) => {
	const [selectedValue, setSelectedValue] = useState<any>();
	const [openSubItems, setOpenSubItems] = useState<{ [key: string]: boolean }>({});
	const [subMenuPositions, setSubMenuPositions] = useState<{
		[key: string]: { top: number; left: number; width: number };
	}>({});
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [selectedSubItemByParent, setSelectedSubItemByParent] = useState<Record<string, string>>({});
	const suppressNextCloseRef = useRef(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const { t } = useTranslation("translation", { keyPrefix: "account.usage" });
	const theme = useTheme();

	const toggleSubItems = (itemKey: string, anchorEl?: HTMLElement | null) => {
		const willOpen = !openSubItems[itemKey];
		setOpenSubItems(willOpen ? { [itemKey]: true } : {});
		if (anchorEl) {
			const rect = anchorEl.getBoundingClientRect();
			setSubMenuPositions(willOpen ? { [itemKey]: { top: rect.top, left: rect.right, width: rect.width } } : {});
		}
	};

	const changeHandler = (e: SelectChangeEvent<T>, child: ReactNode) => {
		if (e.target.value === "noneOption") {
			e.target.value = "";
		}
		rest.onChange && rest.onChange(e, child);
	};

	const emitChange = (newValue: T, childItem: DropDownItem<T>) => {
		const syntheticEvent = { target: { value: newValue } } as unknown as SelectChangeEvent<T>;
		rest.onChange?.(syntheticEvent, childItem as unknown as ReactNode);
		// Track subitem selection per parent internally for highlight state
		if (childItem && (childItem as any).label) {
			const parentKey = (!Array.isArray(selectedValue) ? selectedValue?.value : undefined) as any;
			if (parentKey !== undefined && parentKey !== null) {
				setSelectedSubItemByParent((prev) => ({
					...prev,
					[String(parentKey)]: String((childItem as any).value),
				}));
			}
		} else if (!selectedSubItemByParent[String(newValue)]) {
			setSelectedSubItemByParent((prev) => ({
				...prev,
				[String(newValue)]: childItem.defaultSubItemValue as string,
			}));
		}
	};

	useEffect(() => {
		if (value !== null && value !== undefined) {
			setSelectedValue(getSelectedValue);
		} else {
			setSelectedSubItemByParent({});
		}
	}, [value]);

	useEffect(() => {
		if (selectedValue?.subItems && !selectedSubItemByParent[String(selectedValue.value)]) {
			setSelectedSubItemByParent((prev) => ({
				...prev,
				[String(selectedValue.value)]: selectedValue.defaultSubItemValue as string,
			}));
		}
	}, [selectedValue]);

	useLayoutEffect(() => {
		if (isMenuOpen && selectedValue?.subItems && Object.keys(openSubItems).length === 0) {
			requestAnimationFrame(() => {
				setTimeout(() => {
					const parentItemKey = String(selectedValue.value);
					const parentItemEl = document.querySelector(
						`[role="option"][data-value="${parentItemKey}"]`,
					) as HTMLElement;
					if (parentItemEl) {
						toggleSubItems(parentItemKey, parentItemEl);
					}
				}, 500);
			});
		}
	}, [isMenuOpen]);

	useEffect(() => {
		if (items.length) {
			onItemsLoad?.();
			setSelectedValue(getSelectedValue);
			if (inputRef.current) {
				inputRef.current.focus();
			}
		}
	}, [items]);

	useEffect(() => {
		setSelectedValue(getSelectedValue);
	}, [theme]);

	const getSelectedValue = () =>
		Array.isArray(value)
			? items.filter((item) => value.includes(item.value))
			: items.find((item) => item.value === value);

	const hasSelectedValue = Array.isArray(selectedValue)
		? selectedValue.length > 0
		: selectedValue !== undefined && selectedValue !== null;

	return (
		<StyledFormControl
			className={clsx(className, styles.formControl)}
			width={width}
			height={selectedValue?.length > 0 ? "auto" : height}
			minHeight={minHeight}
			maxHeight={maxHeight}
		>
			<StyledSelect
				displayEmpty={!!rest.placeholder}
				value={typeof value === "number" && isNaN(value) ? "" : (value ?? "")}
				IconComponent={loading ? DropDownLoading : ExpandMoreRoundedIcon}
				hideArrowIcon={hideArrowIcon || (disabled && !loading)}
				disabled={disabled}
				renderValue={() =>
					disabled ? (
						selectedValue?.customSelectedKey || selectedValue?.key?.toString() || rest.placeholder
					) : hasSelectedValue ? (
						<>
							{Array.isArray(selectedValue) ? (
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
									{selectedValue.map((item: DropDownItem<T>) => (
										<EditableChip
											key={item.key}
											label={item.key}
											onEdit={
												handleEditItem
													? (newKey) => {
															handleEditItem?.(item.value as any, newKey);
															setSelectedValue(
																selectedValue.map((selectedItem) =>
																	selectedItem.value === item.value
																		? { ...selectedItem, key: newKey }
																		: selectedItem,
																),
															);
														}
													: undefined
											}
											onDelete={() => {
												handleDelete?.(item.value as any);
												setSelectedValue(
													selectedValue.filter(
														(selectedItem) => selectedItem.value !== item.value,
													),
												);
											}}
										/>
									))}
								</Box>
							) : (
								<Box className={styles.inputStyle}>
									{selectedValue.icon ?? selectedIcon}
									<Typography
										style={{
											fontWeight: labelFontWeight ? labelFontWeight : "",
											fontSize: labelFontSize ? labelFontSize : "",
										}}
										className={styles.lableStyle}
									>
										{selectedValue.customSelectedKey || selectedValue.key}
									</Typography>
								</Box>
							)}
						</>
					) : icon ? (
						<Box className={styles.inputStyle}>
							{icon}
							<Typography
								style={{
									fontWeight: labelFontWeight ? labelFontWeight : "",
									fontSize: labelFontSize ? labelFontSize : "",
								}}
								className={clsx(styles.lableStyle, { [styles.placeholderBold]: rest.placeholderBold })}
							>
								{rest.placeholder}
							</Typography>
						</Box>
					) : (
						<Typography
							className={clsx(styles.lableStyle, { [styles.placeholderBold]: rest.placeholderBold })}
						>
							{rest.placeholder}
						</Typography>
					)
				}
				MenuProps={{
					style: {
						marginTop: 4,
						maxHeight: menuMaxHeight,
						maxWidth: menuMaxWidth,
					},
					PaperProps: {
						style: {
							width: menuWidth,
						},
					},
					MenuListProps: {
						onScroll: () => setOpenSubItems({}),
						onWheel: () => setOpenSubItems({}),
					},
				}}
				input={border ? <SelectInputBase border={border} /> : undefined}
				classes={{
					root: disabled ? styles.disabledSelect : "",
				}}
				{...rest}
				onChange={changeHandler}
				open={isMenuOpen}
				onOpen={(event) => {
					setIsMenuOpen(true);
					rest?.onOpen && rest?.onOpen(event);
				}}
				onClose={(event) => {
					rest?.onClose && rest?.onClose(event);
					if (suppressNextCloseRef.current) {
						suppressNextCloseRef.current = false;
						setIsMenuOpen(true);
						return;
					}
					setIsMenuOpen(false);
				}}
			>
				{searchable && (
					<DebouncedInput
						searchValue={searchValue}
						onChange={onSearchChange}
						className={styles.customBriaInput}
						debounceDuration={debounceSearch ? 500 : 0}
						onKeyDown={(e) => e.stopPropagation()}
						height="36px"
						searchIcon={searchIcon}
						placeHolder={SearchPlaceholder}
						inputRef={inputRef}
					/>
				)}
				{loading && <StyledMenuItem disabled>Loading..</StyledMenuItem>}
				{(!loading && !rest.multiple && !selectMultipleCheckbox && addNoneOption
					? [
							{
								key: "None",
								value: "noneOption",
								icon: null,
							},
							...items,
						]
					: items
				).map((item: DropDownItem) => {
					const isNotActive =
						activeItems.length > 0 && !activeItems.includes(item.key) && item.key !== t("selectAll");
					const { key, ...menuItemProps } = {
						key: `${String(item.value)}::${String(item.key)}`,
						value: item.value as any,
						disabled: item.disabled || disabled,
						className: item.className,
						tooltip: item.tooltip,
					};

					const hasSubItems = item.subItems && item.subItems.length > 0;
					const isSubItemsOpen = openSubItems[item.value];

					const content = (
						<>
							{selectMultipleCheckbox && (
								<BriaCheckbox
									checked={value && Array.isArray(value) && value.indexOf(item.value as string) > -1}
								/>
							)}
							{item.customItem ? (
								item.customItem
							) : item.icon ? (
								<Box className={styles.itemContainer}>
									{item.icon}
									{item.key}
								</Box>
							) : (
								<span
									className={clsx(styles.itemText, item.value === "noneOption" && styles.noneItem)}
									dangerouslySetInnerHTML={{ __html: item.key }}
								/>
							)}
							{item.badge && (
								<Box
									className={`${styles.badge} ${
										item.badgeType === "secondary" ? styles.badgeSecondary : ""
									}`}
								>
									{item.badge}
								</Box>
							)}
							{hasSubItems && (
								<ExpandMore
									className={clsx(styles.expandIcon, { [styles.expanded]: isSubItemsOpen })}
								/>
							)}
						</>
					);

					const wrappedContent = item.tooltip ? (
						<BriaTooltip
							className={styles.tooltip}
							title={item.tooltip}
							displayOn="hover"
							noWrapper={true}
							{...item.tooltipProps}
						>
							<Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>{content}</Box>
						</BriaTooltip>
					) : (
						content
					);

					return isNotActive ? (
						<HiddenMenuItem key={key} {...menuItemProps}>
							{item.key}
						</HiddenMenuItem>
					) : (
						<StyledMenuItem
							key={key}
							{...menuItemProps}
							data-value={item.value}
							onMouseDown={hasSubItems ? (e) => e.preventDefault() : undefined}
							onClick={
								hasSubItems
									? (e) => {
											e.preventDefault();
											e.stopPropagation();
											const menuItemEl =
												(e.currentTarget.closest('[role="menuitem"]') as HTMLElement) ||
												(e.currentTarget as HTMLElement);
											toggleSubItems(item.value, menuItemEl);
											suppressNextCloseRef.current = true;
											emitChange(item.value as T, item as DropDownItem<T>);
											setIsMenuOpen(true);
										}
									: () => {
											setOpenSubItems({});
											!rest.multiple && setIsMenuOpen(false);
										}
							}
						>
							{wrappedContent}
							{hasSubItems && isSubItemsOpen && (
								<Box
									className={styles.subItemsFlyout}
									style={{
										top: subMenuPositions[item.value]?.top,
										left: subMenuPositions[item.value]?.left,
										minWidth: subMenuPositions[item.value]?.width || 280,
									}}
								>
									{item.subItems!.map((subItem) => {
										const subMenuItemProps = {
											key: `${String(item.value)}::${String(subItem.value)}::${String(
												subItem.key,
											)}`,
											value: subItem.value as any,
											disabled: subItem.disabled || disabled,
											className: clsx(subItem.className, styles.subMenuItem),
										};

										const subContent = (
											<>
												{selectMultipleCheckbox && (
													<BriaCheckbox
														checked={
															value &&
															Array.isArray(value) &&
															value.indexOf(subItem.value as string) > -1
														}
													/>
												)}
												{subItem.customItem ? (
													subItem.customItem
												) : subItem.icon ? (
													<Box className={styles.itemContainer}>
														{subItem.icon}
														{subItem.key}
													</Box>
												) : (
													<span
														className={styles.itemText}
														dangerouslySetInnerHTML={{ __html: subItem.key }}
													/>
												)}
												{subItem.badge && <Box className={styles.badge}>{subItem.badge}</Box>}
											</>
										);

										const isParentSelected =
											!Array.isArray(selectedValue) && selectedValue?.value === item.value;
										const isSubItemSelected =
											isParentSelected &&
											selectedSubItemByParent[String(item.value)] !== undefined &&
											String(selectedSubItemByParent[String(item.value)]) ===
												String(subItem.value);

										return (
											<StyledMenuItem
												{...subMenuItemProps}
												selected={isSubItemSelected}
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													emitChange(subItem.value as T, subItem as DropDownItem<T>);
													setIsMenuOpen(false);
												}}
											>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
														width: "100%",
													}}
												>
													{subContent}
													{isSubItemSelected && (
														<Check fontSize="small" sx={{ marginLeft: "auto" }} />
													)}
												</Box>
											</StyledMenuItem>
										);
									})}
								</Box>
							)}
						</StyledMenuItem>
					);
				})}
				{endStickyItem && (
					<>
						<Divider className={styles.menuDivider} />
						<MenuItem className={styles.endStickyItem}>
							<Box className={styles.innerEndStickyItem}>{endStickyItem}</Box>
						</MenuItem>
					</>
				)}
			</StyledSelect>
		</StyledFormControl>
	);
};

export default observer(BriaDropdown);

export const StyledFormControl = styled(FormControl)(
	({
		width,
		height,
		maxHeight,
		minHeight,
	}: {
		width?: string;
		height?: string;
		maxHeight: string | undefined;
		minHeight?: string;
	}) => ({
		width: width ?? "auto",
		"& .MuiInputBase-root": {
			height: height ?? "40px",
			maxHeight: maxHeight ?? "unset",
			minWidth: "120px",
			fontSize: "14px",
			fontWeight: "400",
			justifyContent: "center",
		},
		"& .MuiInputBase-input": {
			minHeight: minHeight,
		},
		"& .MuiSelect-select": {
			minHeight: "16px",
			padding: "12px 14px",
		},
	}),
);

const SelectInputBase = styled(InputBase)(({ border }: { border?: string }) => ({
	border: border ? border : "initial",
	"& > div": {
		padding: "16.5px 14px",
	},
}));

type CustomSelectProps = SelectProps<any> & {
	hideArrowIcon?: boolean;
};

const StyledSelect = styled(Select<any>, { shouldForwardProp: (props) => props != "hideArrowIcon" })<CustomSelectProps>(
	({ theme, hideArrowIcon }: { theme: Theme; hideArrowIcon?: boolean }) => ({
		width: "auto",
		"&.Mui-focused": {
			".MuiSvgIcon-root": {
				fill: theme.palette.mode === "dark" ? "var(--secondary-color)" : theme.palette.primary.light,
			},
		},
		".MuiSvgIcon-root": {
			color: "var(--primary-text-color)",
		},
		".MuiSelect-icon": {
			display: hideArrowIcon ? "none" : "block",
		},
	}),
);

export const StyledMenuItem = styled(MenuItem)({
	fontWeight: 500,
	paddingTop: 8,
	paddingBottom: 8,
	fontSize: "14px",
	color: "var(--secondary-color)",
	textWrap: "wrap",
	"&.Mui-selected": {
		color: "var(--primary-text-color)",
		fontWeight: "bold",
		backgroundColor: "var(--mui-menuitem-background-selected) !important",
	},
});

const DropDownLoading = styled(CircularProgress)(({ theme }) => ({
	color: `${theme.palette.primary.light} !important`,
	width: "1em !important",
	height: "1em !important",
	right: "13px !important",
}));

const HiddenMenuItem = styled(MenuItem)({
	display: "none",
});
